import React from 'react';
import s from './TriageForm.module.css';

function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className={s.opts}>
      {options.map(opt => (
        <label key={opt.value} className={s.opt}>
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
          <span className={s.optSpan}>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function CheckGroup({ options, checked, onToggle }) {
  return (
    <div className={s.opts}>
      {options.map(opt => (
        <label key={opt.value} className={s.opt}>
          <input
            type="checkbox"
            value={opt.value}
            checked={checked.includes(opt.value)}
            onChange={() => onToggle(opt.value)}
          />
          <span className={s.optSpan}>
            {opt.label}
            {opt.tier !== undefined && (
              <span className={s.tier}>T{opt.tier}</span>
            )}
          </span>
        </label>
      ))}
    </div>
  );
}

function Block({ num, label, hint, children }) {
  return (
    <div className={s.block}>
      <div className={s.lab}>
        <span className={s.num}>{num}</span>
        {label}
      </div>
      {hint && <p className={s.hint}>{hint}</p>}
      {children}
    </div>
  );
}

const TYPE_OPTS = [
  { value: 'micro',      label: 'Micro shop / single outlet' },
  { value: 'sme',        label: 'SME trader / distributor / manufacturer' },
  { value: 'fleet',      label: 'Logistics / fleet' },
  { value: 'chain',      label: 'Multi-location / franchise' },
  { value: 'biller',     label: 'Recurring biller / institution' },
  { value: 'enterprise', label: 'Enterprise / PSU' },
  { value: 'partner',    label: 'Platform / aggregator (white-label)' },
];

const GEO_OPTS = [
  { value: 'uae',   label: 'UAE' },
  { value: 'metro', label: 'India — metro' },
  { value: 'tier',  label: 'India — tier 2/3' },
];

const HOOK_OPTS = [
  { value: 'record',    label: 'Basic record-keeping · voice cash-log',      tier: 0 },
  { value: 'statement', label: 'Statement reconciliation / financial overview', tier: 0 },
  { value: 'utility',   label: 'Bulk utility payments',                       tier: 1 },
  { value: 'fastag',    label: 'FASTag / fleet recharges',                    tier: 1 },
  { value: 'collect',   label: 'Recurring / intent-based collections',         tier: 1 },
  { value: 'xborder',   label: 'Cross-border collections',                    tier: 2 },
  { value: 'vendor',    label: 'Vendor payments',                             tier: 2 },
  { value: 'airecon',   label: 'AI reconciliation at scale',                  tier: 3 },
];

const VOLUME_OPTS = [
  { value: 'neg',   label: 'Negligible (<50)' },
  { value: 'small', label: 'Small (50–500)' },
  { value: 'mid',   label: 'Meaningful (500–5k)' },
  { value: 'high',  label: 'High (5k+)' },
];

const MONEY_OPTS = [
  { value: 'thin',   label: 'Thin — free hook / cheap sub only' },
  { value: 'mod',    label: 'Moderate — sub + some commission' },
  { value: 'strong', label: 'Strong — high volume / contract' },
];

const PAIN_OPTS = [
  { value: 'core', label: 'Core — waits 30–120 days to get paid' },
  { value: 'some', label: 'Some' },
  { value: 'none', label: 'None' },
];

const BUYER_OPTS = [
  { value: 'direct',      label: 'Direct — founder / CFO / finance head' },
  { value: 'influencer',  label: 'Influencer only' },
  { value: 'none',        label: 'None yet' },
];

const EFFORT_OPTS = [
  { value: 'low',  label: 'Low — self-serve' },
  { value: 'med',  label: 'Medium' },
  { value: 'high', label: 'High — custom integration' },
];

export default function TriageForm({ form, setField, toggleHook }) {
  return (
    <div className={s.panel}>
      <Block num={1} label="Who are they?" hint="Pick the closest business shape.">
        <RadioGroup
          name="type"
          options={TYPE_OPTS}
          value={form.type}
          onChange={v => setField('type', v)}
        />
      </Block>

      <Block num={2} label="Where are they?">
        <RadioGroup
          name="geo"
          options={GEO_OPTS}
          value={form.geo}
          onChange={v => setField('geo', v)}
        />
      </Block>

      <Block
        num={3}
        label="Which FiniGenie hooks fit their pain?"
        hint="Tick every one that genuinely applies — the highest-value one becomes the wedge."
      >
        <CheckGroup
          options={HOOK_OPTS}
          checked={form.hooks}
          onToggle={toggleHook}
        />
      </Block>

      <Block
        num={4}
        label="Scale on the relevant metric"
        hint="Invoices, bills, recharges or collection transactions per month — whichever fits the wedge above."
      >
        <RadioGroup
          name="volume"
          options={VOLUME_OPTS}
          value={form.volume}
          onChange={v => setField('volume', v)}
        />
      </Block>

      <Block
        num={5}
        label="Monetisation potential"
        hint="What could this realistically pay us — subscription, transaction commission, or a contract?"
      >
        <RadioGroup
          name="money"
          options={MONEY_OPTS}
          value={form.money}
          onChange={v => setField('money', v)}
        />
      </Block>

      <Block num={6} label="Working-capital pain (deliver-first?)">
        <RadioGroup
          name="pain"
          options={PAIN_OPTS}
          value={form.pain}
          onChange={v => setField('pain', v)}
        />
      </Block>

      <Block num={7} label="Reachable economic buyer?">
        <RadioGroup
          name="buyer"
          options={BUYER_OPTS}
          value={form.buyer}
          onChange={v => setField('buyer', v)}
        />
      </Block>

      <Block num={8} label="Effort to onboard / cost to serve">
        <RadioGroup
          name="effort"
          options={EFFORT_OPTS}
          value={form.effort}
          onChange={v => setField('effort', v)}
        />
      </Block>
    </div>
  );
}
